import {
  getPipeexSettings,
  summarizePcfSettingsDtoForDebug,
  type PcfSettingsDto,
  type SettingInfoDto,
} from '../../../../shared/api/pipeex/pcfSettingsClient';
import { traceMappingFlow, warnMappingFlow } from '../mappingTrace';
import { withTimeout } from './asyncTimeout';

export interface CachedProfileSettingsResult {
  dto: PcfSettingsDto | null;
  error: string | null;
}

export interface ProfileSettingsInspectionResult extends CachedProfileSettingsResult {
  cacheHit: boolean;
}

export type ProfileInspectionCandidate = {
  profile: SettingInfoDto;
  dto: PcfSettingsDto | null;
  error: string | null;
};

const MAX_PROFILE_SETTINGS_CACHE_ENTRIES = 48;
const PROFILE_SETTINGS_INSPECTION_TIMEOUT_MS = 8_000;

const profileSettingsCache = new Map<string, CachedProfileSettingsResult>();
const profileSettingsInFlight = new Map<string, Promise<CachedProfileSettingsResult>>();

function getProfileTraceKey(profile: SettingInfoDto): string {
  return `${profile.name}::${profile.settingsType}`;
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Profile could not be inspected.';
}

export function buildProfileSignature(profiles: SettingInfoDto[]): string {
  return profiles
    .map((profile) => `${profile.name}::${profile.settingsType}::${profile.changedOn ?? ''}`)
    .sort()
    .join('|');
}

function buildProfileSettingsCacheKey(profile: SettingInfoDto): string {
  return `${profile.name}::${profile.settingsType}::${profile.changedOn ?? ''}`;
}

function isProfileSettingsCacheable(profile: SettingInfoDto): boolean {
  return typeof profile.changedOn === 'string' && profile.changedOn.trim().length > 0;
}

function rememberProfileSettingsResult(
  cacheKey: string,
  result: CachedProfileSettingsResult
): void {
  profileSettingsCache.delete(cacheKey);
  profileSettingsCache.set(cacheKey, result);

  while (profileSettingsCache.size > MAX_PROFILE_SETTINGS_CACHE_ENTRIES) {
    const oldestKey = profileSettingsCache.keys().next().value;
    if (!oldestKey) {
      break;
    }

    profileSettingsCache.delete(oldestKey);
  }
}

async function fetchProfileSettings(profile: SettingInfoDto): Promise<CachedProfileSettingsResult> {
  const profileKey = getProfileTraceKey(profile);
  const startedAt = performance.now();
  const stallTimer = globalThis.setTimeout(() => {
    warnMappingFlow('project profile settings inspection fetch still pending', {
      profileKey,
      timeoutMs: PROFILE_SETTINGS_INSPECTION_TIMEOUT_MS,
      elapsedMs: Math.round(performance.now() - startedAt),
    });
  }, PROFILE_SETTINGS_INSPECTION_TIMEOUT_MS);
  traceMappingFlow('project profile settings inspection fetch start', {
    profileKey,
    changedOnPresent: isProfileSettingsCacheable(profile),
    changedOn: profile.changedOn ?? null,
  });
  try {
    const result = {
      dto: await withTimeout(
        getPipeexSettings(profile.name, profile.settingsType),
        PROFILE_SETTINGS_INSPECTION_TIMEOUT_MS,
        `Profile "${profile.name}" settings inspection`
      ),
      error: null,
    };
    traceMappingFlow('project profile settings inspection fetch complete', {
      profileKey,
      success: true,
      elapsedMs: Math.round(performance.now() - startedAt),
      summary: summarizePcfSettingsDtoForDebug(result.dto),
    });
    return result;
  } catch (error) {
    const message = getErrorMessage(error);
    warnMappingFlow('project profile settings inspection fetch failed', {
      profileKey,
      error: message,
      elapsedMs: Math.round(performance.now() - startedAt),
    });
    return {
      dto: null,
      error: message,
    };
  } finally {
    globalThis.clearTimeout(stallTimer);
  }
}

export async function loadProfileSettings(
  profile: SettingInfoDto
): Promise<ProfileSettingsInspectionResult> {
  if (!isProfileSettingsCacheable(profile)) {
    traceMappingFlow('project profile settings inspection cache skipped', {
      profileKey: getProfileTraceKey(profile),
      reason: 'missingChangedOn',
    });
    return {
      ...(await fetchProfileSettings(profile)),
      cacheHit: false,
    };
  }

  const cacheKey = buildProfileSettingsCacheKey(profile);
  const cached = profileSettingsCache.get(cacheKey);
  if (cached) {
    traceMappingFlow('project profile settings inspection cache hit', {
      profileKey: getProfileTraceKey(profile),
      cacheKey,
    });
    return { ...cached, cacheHit: true };
  }

  let inFlight = profileSettingsInFlight.get(cacheKey);
  if (!inFlight) {
    traceMappingFlow('project profile settings inspection cache miss', {
      profileKey: getProfileTraceKey(profile),
      cacheKey,
    });
    inFlight = fetchProfileSettings(profile);
    profileSettingsInFlight.set(cacheKey, inFlight);
  } else {
    traceMappingFlow('project profile settings inspection joined in-flight request', {
      profileKey: getProfileTraceKey(profile),
      cacheKey,
    });
  }

  const result = await inFlight;
  profileSettingsInFlight.delete(cacheKey);
  if (result.dto) {
    rememberProfileSettingsResult(cacheKey, result);
  }

  return { ...result, cacheHit: false };
}

export function readCachedProfileInspection(
  profiles: SettingInfoDto[]
): ProfileInspectionCandidate[] | null {
  const candidates: ProfileInspectionCandidate[] = [];

  for (const profile of profiles) {
    if (!isProfileSettingsCacheable(profile)) {
      return null;
    }

    const cached = profileSettingsCache.get(buildProfileSettingsCacheKey(profile));
    if (!cached) {
      return null;
    }

    candidates.push({
      profile,
      dto: cached.dto,
      error: cached.error,
    });
  }

  return candidates;
}

export const resetProfileSettingsInspectionCacheForTests = (): void => {
  profileSettingsCache.clear();
  profileSettingsInFlight.clear();
};
